import { Box, Card, CardActionArea, CardContent, Chip, Grid, Typography } from '@mui/material';
import React from 'react';

import { getServiceCardPrice } from '../../utils/serviceCardPrice';

// Declared as a type alias rather than an interface so it carries an implicit
// index signature and can be handed straight to `getServiceCardPrice`, which
// accepts the same loose service shape the rest of the pricing helpers use.
type SSRService = {
  id: string;
  name: string;
  brand_name?: string;
  is_select?: boolean;
  category?: string;
  description?: string;
  base_price?: number; // cents
  price_range_min?: number; // cents
  price_range_max?: number; // cents
  // Pass-through pricing fields. `getServiceCardPrice` prefers the variant /
  // default_pricing shapes when present so the SSR pass resolves the same
  // number the hydrated card will, rather than a denormalized approximation.
  pricing_model?: string;
  default_pricing?: Record<string, any>;
  variants?: Record<string, any>;
  default_variant_key?: string;
  frequencies?: Record<string, any>[];
  contract_term_months?: number[];
  default_term_months?: number;
  price_min_cents?: number;
  price_max_cents?: number;
  price_cents?: number;
  // PR-9 tile rail. When tile_unit_basis !== 'flat', render a
  // "From $X per <unit>" rail instead of the price range. Services that
  // scale with a natural unit (phone lines, seats, users, sites) set
  // these via the reshape/seed pipeline; flat-priced services leave
  // tile_unit_basis='flat' and keep today's behavior.
  tile_unit_basis?: 'flat' | 'per_line' | 'per_user' | 'per_seat' | 'per_site';
  tile_price_cents?: number | null;
  tile_unit_label?: string | null;
};

interface ServiceGridSSRProps {
  services: SSRService[];
  catalogInstance?: string; // if set, links go to /c/[catalogInstance]/services/[id]
}

const SelectBadge = () => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '999px', border: '1px solid #D4AF37', bgcolor: 'rgba(212, 175, 55, 0.12)', color: '#8c6a00', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', width: 'fit-content' }}>
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2.5L14.95 8.48L21.55 9.44L16.78 14.08L17.9 20.64L12 17.54L6.1 20.64L7.22 14.08L2.45 9.44L9.05 8.48L12 2.5Z" fill="#D4AF37" />
    </svg>
    Select
  </Box>
);

// Price formatting is deliberately NOT implemented here. This component
// renders the pre-hydration / crawler-visible HTML for the same cards that
// `ServiceCardNew` -> `SimplePricingDisplay` renders once React takes over.
// When the two had separate formatters they drifted: this one rounded to
// whole dollars and emitted an en-dash range, the hydrated one used two
// decimals and a hyphen — so the price a buyer saw changed as the page
// hydrated, and crawlers indexed a number the UI never showed. Both now call
// `getServiceCardPrice`, which makes parity structural instead of a
// convention someone has to remember.

export function ServiceGridSSR({ services, catalogInstance }: ServiceGridSSRProps) {
  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3, maxWidth: 1200, mx: 'auto' }}>
      <Grid container spacing={3}>
        {services.map((svc) => {
          const href = catalogInstance ? `/c/${catalogInstance}/services/${svc.id}` : `/services/${svc.id}`;
          const { headline: price, schedule, fees } = getServiceCardPrice(svc);
          const desc = svc.description
            ? svc.description.slice(0, 120) + (svc.description.length > 120 ? '…' : '')
            : '';

          return (
            <Grid item xs={12} sm={6} md={4} key={svc.id}>
              <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea component="a" href={href} sx={{ flexGrow: 1 }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {svc.category && (
                      <Chip label={svc.category} size="small" sx={{ alignSelf: 'flex-start' }} />
                    )}
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                      {svc.name}
                    </Typography>
                    {svc.brand_name && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {svc.is_select === true && <SelectBadge />}
                        <Typography variant="caption" color="text.secondary">
                          by {svc.brand_name}
                        </Typography>
                      </Box>
                    )}
                    {desc && (
                      <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                        {desc}
                      </Typography>
                    )}
                    {price && (
                      <Box sx={{ mt: 'auto' }}>
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700 }}>
                          {price}
                        </Typography>
                        {/* Escalation must be visible pre-hydration too — this
                            is the markup crawlers and no-JS buyers actually read. */}
                        {schedule && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem', display: 'block', fontWeight: 600 }}
                          >
                            {schedule}
                          </Typography>
                        )}
                        {fees.map((fee) => (
                          <Typography
                            key={fee}
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem', display: 'block' }}
                          >
                            {fee}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
